import { activeFacebookPageIdSelector } from '../selectors/viewSelectors';
import { Dispatch } from 'redux';
import LeadsController from '../controllers/LeadsCotroller';
import commonHandler from '../errorHandlers/common-handler';
import {
  LeadsSearchResponse,
  LeadsSearchQuery,
  SearchAttributes,
  ConvertationRequest,
  ConvertLeadsResponse,
} from '../interfaces/leads';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';

class LeadsCRUDActions extends CrudActionContainer {
  retrieveLeadsBySearchAttributes: string;
  resetLeadsAction: string;
  busyAction: string;
  setFreezedCreditsCount: string;

  constructor() {
    super('LEADS');
    this.retrieveLeadsBySearchAttributes = super.makeActionKey(
      'RETRIEVED_LEADS_BY_SEACH_ATTRIBUTES',
    );
    this.resetLeadsAction = this.makeActionKey('RESET_SEARCHED_LEADS');
    this.busyAction = this.makeActionKey('ENTITY_BUSY');
    this.setFreezedCreditsCount = this.makeActionKey(
      'SET_FREEZED_CREDITS_COUNT',
    );
  }

  retrieveAttributes() {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));
      try {
        const response: SearchAttributes =
          await LeadsController.getAllAttributes();
        dispatch(this.retrievedAll(response));
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        this.unbusy();
      }
    };
  }

  retrieveLocationAttributes(query: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response: SearchAttributes =
          await LeadsController.retrieveLocationAttributes(query);
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      }
    };
  }

  searchLeads(params: LeadsSearchQuery) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      dispatch(this.busy(this.retrieveAllAction));
      const state = getState();
      const fbPageId = activeFacebookPageIdSelector(state);
      try {
        const response: LeadsSearchResponse = await LeadsController.searchLeads(
          { fbPageId, ...params },
        );
        dispatch({
          type: this.retrieveLeadsBySearchAttributes,
          contacts: response,
        });
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        this.unbusy();
      }
    };
  }

  convertLeadsToContacts(fbPageId: number, params: ConvertationRequest) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response: ConvertLeadsResponse =
          await LeadsController.convertSearchedLeadsToContacts(
            fbPageId,
            params,
          );
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        this.unbusy();
      }
    };
  }

  retrieveConvertationRequests(
    fbPageId: number,
    status?: 'processing' | 'queued',
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response: ConvertLeadsResponse[] =
          await LeadsController.getConvertationRequests(fbPageId, status);
        const initialValue = 0;

        if (response) {
          const freezedCretitsSum = response
            ?.map(i => i.freezedCredits)
            ?.reduce(
              (accumulator, currentValue) =>
                (accumulator || 0) + (currentValue || 0),
              initialValue,
            );

          dispatch({
            type: this.setFreezedCreditsCount,
            payload: freezedCretitsSum,
          });
        }

        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        this.unbusy();
      }
    };
  }

  resetLeads() {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch({
        type: this.resetLeadsAction,
      });
    };
  }

  keepBusy() {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch({
        type: this.busyAction,
      });
    };
  }
}

const leadsCRUDActions = new LeadsCRUDActions();

export default leadsCRUDActions;

export const useLeadsCRUDActions = getActionHookBuilder(leadsCRUDActions, [
  'retrieveAttributes',
  'retrieveLocationAttributes',
  'searchLeads',
  'resetLeads',
  'convertLeadsToContacts',
  'retrieveConvertationRequests',
  'keepBusy',
]);
